import { useEffect, useMemo, useState } from "react";
import type { GroupBase, StylesConfig } from "react-select";

import type { RouterOutputs } from "@calcom/trpc/react";
import { trpc } from "@calcom/trpc/react";
import { Select } from "@calcom/ui";

import { NewUsersIcon } from "@components/Icons";
import { MissingPatientPrompt } from "@components/patients/MissingPatientPrompt";

export type OfficePatientSelectOption = RouterOutputs["viewer"]["patients"]["getNameList"][number];

export type OfficePatientSelectProps = {
  officeId: number;
  value?: number | null;
  onChange: (patientId: number | undefined, option?: OfficePatientSelectOption) => void;
  placeholder?: string;
  className?: string;
  hasIcon?: boolean;
  isDisabled?: boolean;
  instanceId?: string;
  /** Appended after server options (deduped by value). */
  extraOptions?: OfficePatientSelectOption[];
  styles?: StylesConfig<OfficePatientSelectOption, false, GroupBase<OfficePatientSelectOption>>;
  /** When a search returns no patients, offer NexHealth sync and manual create. */
  showMissingPatientHelp?: boolean;
};

const SEARCH_DEBOUNCE_MS = 800;

export function OfficePatientSelect({
  officeId,
  value,
  onChange,
  placeholder = "Search Account",
  className,
  hasIcon = false,
  isDisabled,
  instanceId,
  extraOptions,
  styles,
  showMissingPatientHelp = false,
}: OfficePatientSelectProps) {
  const [search, setSearch] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [createdOptions, setCreatedOptions] = useState<OfficePatientSelectOption[]>([]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [search]);

  const {
    data: patientOptions,
    isLoading,
    isFetching,
  } = trpc.viewer.patients.getNameList.useQuery(
    {
      officeId,
      patientName: debouncedSearchQuery || undefined,
      patientId: debouncedSearchQuery ? undefined : value ?? undefined,
    },
    {
      enabled: !!officeId,
      refetchInterval: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }
  );

  const options = useMemo(() => {
    const base = patientOptions ?? [];
    const extras = [...(extraOptions ?? []), ...createdOptions];
    if (!extras.length) return base;
    const ids = new Set(base.map((o) => o.value));
    return [...base, ...extras.filter((e) => !ids.has(e.value))];
  }, [patientOptions, extraOptions, createdOptions]);

  const showMissingPatient =
    showMissingPatientHelp &&
    debouncedSearchQuery.trim().length > 0 &&
    !isLoading &&
    !isFetching &&
    (patientOptions?.length ?? 0) === 0;

  const selectedOption =
    value != null
      ? options.find((p) => p.value === value) ?? patientOptions?.find((p) => p.value === value)
      : null;

  const select = (
    <Select<OfficePatientSelectOption, false>
      instanceId={instanceId}
      placeholder={placeholder}
      className={className}
      hasIcon={hasIcon}
      isDisabled={isDisabled}
      value={selectedOption}
      options={options}
      onInputChange={(input, meta) => {
        if (meta.action === "input-change") setSearch(input);
      }}
      isLoading={isLoading}
      onChange={(option) => {
        onChange(option?.value, option ?? undefined);
      }}
      styles={styles}
    />
  );

  const missingPatientPrompt = (
    <MissingPatientPrompt
      officeId={officeId}
      visible={showMissingPatient}
      onPatientCreated={(patient) => {
        const option = {
          label: `${patient.firstName} ${patient.lastName}`,
          value: patient.patientId,
        };
        setCreatedOptions((current) => [...current.filter((item) => item.value !== option.value), option]);
        onChange(patient.patientId, option);
        setSearch("");
        setDebouncedSearchQuery("");
      }}
    />
  );

  if (hasIcon) {
    return (
      <div className="w-full">
        <div className="relative w-full">
          {select}
          <div className="pointer-events-none absolute inset-0 left-2 right-auto flex items-center">
            <NewUsersIcon />
          </div>
        </div>
        {missingPatientPrompt}
      </div>
    );
  }

  return (
    <div className="relative w-full">
      {select}
      {missingPatientPrompt}
    </div>
  );
}
